import React, { useState, useEffect } from "react";
import ReactGA from "react-ga4";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useNavigate,
} from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";

import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import ChatLayout from "./layouts/ChatLayout";
import LandingPage from "./pages/LandingPage";
import ResultsPage from "./pages/ResultsPage";
import AdminQueryPage from "./pages/admin/AdminQueryPage";
import SettingsPage from "./pages/SettingsPage";
import CheckoutPage from "./pages/CheckoutPage";
import BrowsePage from "./pages/P2PBrowsePage";
import P2PSellerDashboard from "./pages/P2PSellerDashboard";
import P2PListingDetailPage from "./pages/P2PListingDetailPage";
import P2POffersPage from "./pages/P2POffersPage";
import WalletCallbackPage from "./pages/WalletCallbackPage";
import JsonRenderTestPage from "./pages/JsonRenderTestPage";
import Login from "./components/Auth/Login";
import ModalContextProvider from "./components/contexts/ModalContext";
import { WalletProvider } from "./contexts/WalletContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { BrandProvider } from "./contexts/BrandContext";
import PreviewPanelProvider from "./contexts/PreviewPanelContext";
import ImageUpload from "./components/P2P/ImageUpload";
import ListingReviewForm from "./components/P2P/ListingReviewForm";
import "./index.css";

// Placeholder components for Stripe redirects
function StripeReturnPage() {
  useEffect(() => {
    console.log("Stripe Onboarding Return URL Hit");
  }, []);
  return (
    <div className="p-8 text-center">
      Stripe onboarding complete! Redirecting...
    </div>
  );
}

function StripeRefreshPage() {
  const navigate = useNavigate();
  const apiBaseUrl = process.env.REACT_APP_API || "/api";

  useEffect(() => {
    console.log("Stripe Onboarding Refresh URL Hit - Generating new link...");
    const generateLink = async () => {
      try {
        const response = await fetch(
          `${apiBaseUrl}/secure/p2p/stripe/connect-onboarding`,
          { method: "POST", credentials: "include" }
        );
        if (!response.ok) throw new Error("Failed to get new link");
        const { url } = await response.json();
        if (url) {
          window.location.href = url;
        } else {
          throw new Error("No URL returned");
        }
      } catch (error) {
        console.error("Failed to refresh Stripe onboarding link:", error);
        navigate("/");
      }
    };
    generateLink();
  }, [navigate, apiBaseUrl]);
  return (
    <div className="p-8 text-center">Refreshing Stripe onboarding link...</div>
  );
}

// Load Stripe outside component
const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  : Promise.resolve(null);

function App() {
  // Intercept OAuth code and redirect to backend before React Router mounts
  useEffect(() => {
    const url = new URL(window.location.href);
    const code = url.searchParams.get("code");
    if (code) {
      console.log("[App] Intercepting OAuth code, redirecting to backend callback");
      const state = url.searchParams.get("state") || "/";
      window.location.href = `/api/auth/callback?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}`;
    }
  }, []);

  // Don't render until we've checked for OAuth code
  const url = new URL(window.location.href);
  if (url.searchParams.get("code")) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-500">Authenticating...</div>
      </div>
    );
  }

  return (
    <Router>
      <BrandProvider>
        <AuthProvider>
          <WalletProvider>
            <ModalContextProvider>
              <PreviewPanelProvider>
                <AppContent />
              </PreviewPanelProvider>
            </ModalContextProvider>
          </WalletProvider>
        </AuthProvider>
      </BrandProvider>
    </Router>
  );
}

// Main app content with routes
function AppContent() {
  const { user, isLoading, getAccessToken } = useAuth();
  const navigate = useNavigate();
  const apiBaseUrl = process.env.REACT_APP_API || "/api";

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isProcessingVlm, setIsProcessingVlm] = useState(false);
  const [vlmError, setVlmError] = useState(null);
  const [vlmData, setVlmData] = useState(null);
  const [draftListingId, setDraftListingId] = useState(null);

  useEffect(() => {
    console.log("[AppContent] Mounted, user:", user?.email || "none");
  }, [user]);

  // Track new users with GA
  useEffect(() => {
    if (user) {
      const userAgeInMinutes = (new Date() - new Date(user.createdAt)) / 1000 / 60;
      if (userAgeInMinutes < 5) {
        ReactGA.event({ category: "User", action: "Sign Up", label: user.id });
      }
    }
  }, [user]);

  const handleImagesUploaded = async (imageIds) => {
    if (!imageIds || imageIds.length === 0) {
      setVlmError("No valid images were uploaded. Please try again.");
      return;
    }

    const accessToken = await getAccessToken();
    if (!accessToken) {
      console.error("Token could not be retrieved.");
      setVlmError("Authentication failed or required. Please log in again.");
      return;
    }
    setIsProcessingVlm(true);
    setVlmError(null);
    setVlmData(null);
    setDraftListingId(null);
    try {
      const url = `${apiBaseUrl}/secure/p2p/identify-item`;
      console.log(`Calling API: ${url} with imageIds:`, imageIds);
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ imageIds }),
        credentials: "include",
      });
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          setVlmError(`Authentication error (${response.status}). Please log in again.`);
          return;
        }
        const errorText = await response.text();
        try {
          const errorData = JSON.parse(errorText);
          throw new Error(errorData?.error || `HTTP error! status: ${response.status}`);
        } catch {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      }
      const result = await response.json();
      if (result.success) {
        setVlmData(result.data);
        setDraftListingId(result.draftListingId);
      } else {
        throw new Error(result.error || "Failed to identify item.");
      }
    } catch (error) {
      console.error("Error calling identify-item API:", error);
      setVlmError(`Failed to analyze images: ${error.message}`);
    } finally {
      setIsProcessingVlm(false);
    }
  };

  const handleListingSubmit = async (finalFormData) => {
    const accessToken = await getAccessToken();
    if (!accessToken || !draftListingId) {
      alert("Authentication error or missing data. Please try again.");
      return;
    }
    try {
      const url = `${apiBaseUrl}/secure/p2p/publish-listing/${draftListingId}`;
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(finalFormData),
        credentials: "include",
      });
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          alert("Auth error. Try logging out/in.");
          return;
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData?.error || `HTTP error! status: ${response.status}`);
      }
      const result = await response.json();
      if (result.success) {
        navigate("/");
        setVlmData(null);
        setDraftListingId(null);
      } else {
        throw new Error(result.error || "Failed to publish listing.");
      }
    } catch (error) {
      console.error("Error publishing listing:", error);
      alert(`Error publishing listing: ${error.message}`);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <Routes>
      <Route
        element={
          <ChatLayout
            selectedProduct={selectedProduct}
            setSelectedProduct={setSelectedProduct}
          />
        }
      >
        <Route path="/" element={<LandingPage />} />
        <Route
          path="/admin/query"
          element={
            <ProtectedRoute admin>
              <AdminQueryPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/chats/:sessionId"
          element={
            <ResultsPage
              selectedProduct={selectedProduct}
              setSelectedProduct={setSelectedProduct}
            />
          }
        />
        <Route
          path="/sell"
          element={
            <ProtectedRoute>
              <div className="py-8 px-4">
                {isProcessingVlm && (
                  <div className="text-center">
                    <p className="text-lg">Analyzing your images...</p>
                  </div>
                )}
                {vlmError && (
                  <div className="text-center text-red-500">
                    <p className="text-lg">{vlmError}</p>
                  </div>
                )}
                {!vlmData && !isProcessingVlm && !vlmError && (
                  <ImageUpload onImagesUploaded={handleImagesUploaded} />
                )}
                {vlmData && !isProcessingVlm && (
                  <ListingReviewForm
                    initialData={vlmData}
                    onSubmit={handleListingSubmit}
                  />
                )}
              </div>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <SettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/checkout/:listingId"
          element={
            <ProtectedRoute>
              <Elements stripe={stripePromise} options={{}}>
                <CheckoutPage />
              </Elements>
            </ProtectedRoute>
          }
        />
        <Route path="/p2p/browse" element={<ProtectedRoute><BrowsePage /></ProtectedRoute>} />
        <Route path="/p2p/dashboard" element={<ProtectedRoute><P2PSellerDashboard /></ProtectedRoute>} />
        <Route path="/p2p/listing/:listingId" element={<ProtectedRoute><P2PListingDetailPage /></ProtectedRoute>} />
        <Route path="/p2p/offers" element={<ProtectedRoute><P2POffersPage /></ProtectedRoute>} />
        <Route path="/stripe-onboarding/return" element={<StripeReturnPage />} />
      </Route>
      {/* Standalone routes */}
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/stripe-onboarding/refresh" element={<StripeRefreshPage />} />
      <Route path="/login" element={<Login />} />
      <Route path="/wallet/callback" element={<WalletCallbackPage />} />
      <Route path="/test/json-render" element={<JsonRenderTestPage />} />
    </Routes>
  );
}

const ProtectedRoute = ({ children, admin }) => {
  const { user, isLoading, signIn } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && !user) {
      const path = window.location.pathname + window.location.search;
      console.log(`ProtectedRoute: No user, saving returnTo: ${path}`);
      localStorage.setItem("returnTo", path);
      navigate("/login");
    }
    if (!isLoading && user && admin && !(user.email || "").includes("@pentatonic.com")) {
      console.log("ProtectedRoute: User not admin, redirecting home.");
      navigate("/");
    }
  }, [user, isLoading, admin, navigate, signIn]);

  if (isLoading) {
    return <div className="flex items-center justify-center h-32">Loading...</div>;
  }

  return user && (!admin || (user.email || "").includes("@pentatonic.com"))
    ? children
    : null;
};

export default App;
